import { AxiosError } from 'axios';

import { openErrorNotification } from './notifications';

type ApiError = {
  code: string;
  message: string;
  detail?: any;
  error?: string;
};

const firstLetterUpperCase = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const parseErrorDetails = (content: any): string[] => {
  const detailMessages: string[] = [];

  if (Array.isArray(content)) {
    content.forEach((errorDetailData) => {
      const errorMessage = errorDetailData.ctx?.reason || firstLetterUpperCase(errorDetailData.msg);
      const field = errorDetailData.loc?.[errorDetailData.loc.length - 1];
      detailMessages.push(field ? `${field}: ${errorMessage}` : errorMessage);
    });
  } else if (typeof content === 'string') {
    detailMessages.push(content);
  }

  return detailMessages;
};

// Function for generating error messages as strings
export const generateErrorMessages = (error: AxiosError<ApiError | undefined>): string | string[] => {
  const messages: string[] = [];
  if (error.response) {
    if (error.response.data?.detail || error.response.data?.error) {
      const detailMessages = parseErrorDetails(error.response.data.detail || error.response.data.error);
      messages.push(...detailMessages);
    } else {
      messages.push(`Something went wrong (status ${error.response.status})`);
    }
  } else {
    // Network or other errors
    const networkErrorMsg = error.stack?.includes('Network Error')
      ? 'Something went wrong - check your internet connection and disable VPN!'
      : 'Something went wrong';
    messages.push(networkErrorMsg);
  }

  // Return single string if only one message, otherwise return array
  return messages.length === 1 ? messages[0] : messages;
};

export const isLockoutError = (error: AxiosError<ApiError | undefined>): boolean => {
  return error.response?.status === 429;
};

// Legacy functions kept for backward compatibility
const generateMessage = (content: any): React.ReactNode => {
  let messages: React.ReactNode[] = [];

  if (Array.isArray(content)) {
    messages = content.map((errorDetailData, index) => {
      const errorMessage = errorDetailData.ctx?.reason || firstLetterUpperCase(errorDetailData.msg);
      const field = errorDetailData.loc?.[errorDetailData.loc.length - 1];
      return <div key={index}>{field ? `${field} - ${errorMessage}` : errorMessage}</div>;
    });
  } else if (typeof content === 'string') {
    messages.push(content);
  }

  return messages;
};

export const showApiErrors = (error: AxiosError<ApiError | undefined>): void => {
  let message: React.ReactNode = null;

  if (error.response) {
    if (error.response.data) {
      message = generateMessage(error.response.data.detail);
    }
  }

  const networkErrorMsg = '- check your internet connection and disable VPN!';

  const errorMessage = error.response ? (
    <>
      <div>Request failed with status code {error.response.status}</div> {message}
    </>
  ) : (
    <div>Something went wrong {error.stack?.includes('Network Error') ? networkErrorMsg : null}</div>
  );

  openErrorNotification({
    message: errorMessage,
    duration: 8
  });
};
